import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Download, Link2Off, MessageCircle, Phone, Plus, Search, UserPlus } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { useCatalogos } from '@/components/candidatos/catalogos';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import EmpleadoForm from '@/components/personal/EmpleadoForm';
import SituacionBadge from '@/components/personal/SituacionBadge';
import {
  ETIQUETA_LABEL,
  fechaCorta,
  TIPO_CONTRATO_LABEL,
  type EmpleadoSinFicha,
  type FilaEmpleado,
  type ListadoPersonal,
} from '@/lib/personal';

/**
 * La plantilla: quién hay contratado.
 *
 * Los filtros viven en la URL para que se pueda compartir un enlace con el
 * filtro puesto («los de alta en Zahara»), que es como se usan de verdad.
 *
 * Abajo, si alguien ha dado de alta a una persona en la app de lavandería sin
 * pasar por aquí, aparece con un botón para crearle la ficha. Es lo que impide
 * que el CRM y la lavandería se separen sin que nadie lo note.
 */
export default function PersonalPage() {
  const navigate = useNavigate();
  const { catalogos } = useCatalogos();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') || '';
  const situacion = params.get('situacion') || '';
  const puesto = params.get('puesto') || '';
  const zona = params.get('zona') || '';
  const incluirCuentas = params.get('incluirCuentas') === 'true';

  const [texto, setTexto] = useState(q);
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const [sinFicha, setSinFicha] = useState<EmpleadoSinFicha[]>([]);
  const [creandoFicha, setCreandoFicha] = useState<string | null>(null);

  const ponerFiltro = (clave: string, valor: string) => {
    const siguiente = new URLSearchParams(params);
    if (valor) siguiente.set(clave, valor);
    else siguiente.delete(clave);
    setParams(siguiente, { replace: true });
  };

  // Medio segundo de espera: escribir «Ana» son tres pulsaciones y no hacen
  // falta tres consultas.
  useEffect(() => {
    const t = setTimeout(() => { if (texto !== q) ponerFiltro('q', texto); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto]);

  const { data, loading, error, refresh } = useApi<ListadoPersonal>(
    () => api.getPersonal({ q, situacion, puesto, zona, incluirCuentas }),
    [q, situacion, puesto, zona, incluirCuentas],
  );

  const cargarSinFicha = useCallback(() => {
    api.getPersonalSinFicha().then(setSinFicha).catch(() => setSinFicha([]));
  }, []);
  useEffect(() => { cargarSinFicha(); }, [cargarSinFicha]);

  const crearFichaDe = async (lavanderiaId: string) => {
    setCreandoFicha(lavanderiaId);
    try {
      await api.importarPersonal(lavanderiaId);
      refresh();
      cargarSinFicha();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido crear la ficha');
    } finally {
      setCreandoFicha(null);
    }
  };

  const crear = async (datos: Record<string, unknown>) => {
    const { empleado, aviso } = await api.crearEmpleado(datos);
    setMostrarAlta(false);
    refresh();
    cargarSinFicha();
    if (aviso) alert(aviso);
    navigate(`/equipo/personal/${empleado.id}`);
  };

  const empleados = data?.empleados || [];
  const hayFiltros = !!(q || situacion || puesto || zona);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <p className="text-sm text-sereno-dark/50">
          {data ? (
            <>
              <span className="font-medium text-sereno-dark">{data.total}</span> en la lista ·{' '}
              {data.deAlta} de alta · {data.deBaja} de baja
            </>
          ) : (
            ' '
          )}
        </p>
        <div className="flex items-center gap-2">
          <a
            href="/api/export/personal"
            className="btn-secondary flex items-center gap-1.5 text-sm"
            title="Descargar en Excel. No incluye DNI, Seguridad Social, IBAN ni salarios."
          >
            <Download size={16} />
            <span className="hidden sm:inline">Exportar</span>
          </a>
          <button onClick={() => setMostrarAlta(true)} className="btn-primary flex items-center gap-1.5 text-sm">
            <Plus size={16} />
            <span className="hidden sm:inline">Nuevo empleado</span>
            <span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input
              type="text"
              placeholder="Buscar por nombre, teléfono, email…"
              className="input pl-9"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
          </div>
          <select className="input w-auto" value={situacion} onChange={(e) => ponerFiltro('situacion', e.target.value)}>
            <option value="">Alta y baja</option>
            <option value="alta">Solo de alta</option>
            <option value="baja">Solo de baja</option>
          </select>
          <select className="input w-auto" value={puesto} onChange={(e) => ponerFiltro('puesto', e.target.value)}>
            <option value="">Todos los puestos</option>
            {catalogos.puestos.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          <select className="input w-auto" value={zona} onChange={(e) => ponerFiltro('zona', e.target.value)}>
            <option value="">Todas las zonas</option>
            {catalogos.zonas.map((z) => (
              <option key={z.id} value={z.id}>{z.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-sereno-dark/50" title="Gobernanta y otras cuentas que no son una persona">
            <input
              type="checkbox"
              checked={incluirCuentas}
              onChange={(e) => ponerFiltro('incluirCuentas', e.target.checked ? 'true' : '')}
            />
            Cuentas compartidas
          </label>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <EmptyState title="No se ha podido cargar el personal" description={error} />
      ) : empleados.length === 0 ? (
        <EmptyState
          title={hayFiltros ? 'Nadie encaja con estos filtros' : 'Todavía no hay fichas'}
          description={
            hayFiltros
              ? 'Prueba a quitar alguno.'
              : 'Da de alta a alguien, o crea las fichas de quien ya esté en la app de lavandería.'
          }
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full hidden md:table">
            <thead>
              <tr className="border-b border-gray-100">
                <Th>Nombre</Th>
                <Th>Puesto</Th>
                <Th>Contrato</Th>
                <Th>Situación</Th>
                <Th>Contacto</Th>
              </tr>
            </thead>
            <tbody>
              {empleados.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => navigate(`/equipo/personal/${e.id}`)}
                  className="border-b border-gray-50 hover:bg-sereno-blue/20 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar nombre={e.nombreCompleto} />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{e.nombreCompleto}</p>
                        <Etiquetas fila={e} />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {e.puesto ? (
                      <>
                        <span>{catalogos.puestoLabel[e.puesto] || e.puesto}</span>
                        {e.zona && (
                          <span className="text-sereno-dark/40"> · {catalogos.zonaLabel[e.zona] || e.zona}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-sereno-dark/30">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <Contrato fila={e} />
                  </td>
                  <td className="px-6 py-4">
                    <SituacionBadge situacion={e.situacion} />
                  </td>
                  <td className="px-6 py-4">
                    <Contacto fila={e} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* En móvil la tabla de cinco columnas no cabe sin apretar el nombre. */}
          <div className="md:hidden divide-y divide-gray-50">
            {empleados.map((e) => (
              <div
                key={e.id}
                onClick={() => navigate(`/equipo/personal/${e.id}`)}
                className="p-4 hover:bg-sereno-blue/20 cursor-pointer transition-colors active:bg-sereno-blue/30"
              >
                <div className="flex items-center gap-3">
                  <Avatar nombre={e.nombreCompleto} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{e.nombreCompleto}</p>
                      <SituacionBadge situacion={e.situacion} pequeno />
                    </div>
                    <p className="text-xs text-sereno-dark/40 truncate">
                      {e.puesto ? catalogos.puestoLabel[e.puesto] || e.puesto : 'Sin puesto'}
                      {e.zona ? ` · ${catalogos.zonaLabel[e.zona] || e.zona}` : ''}
                    </p>
                    <Etiquetas fila={e} />
                  </div>
                  <Contacto fila={e} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Los de la lavandería que aún no tienen ficha aquí */}
      {sinFicha.length > 0 && (
        <div className="card mt-6 border border-amber-200 bg-amber-50/50">
          <div className="flex items-start gap-3">
            <UserPlus size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-800">
                {sinFicha.length} en la app de lavandería sin ficha aquí
              </h3>
              <p className="text-xs text-amber-700/80 mt-0.5 mb-3">
                Se han dado de alta allí sin pasar por el CRM. Créales la ficha para que no se queden fuera.
              </p>
              <div className="space-y-1.5">
                {sinFicha.map((s) => (
                  <div key={s.lavanderiaId} className="flex items-center gap-3">
                    <span className="text-sm flex-1">{s.nombre}</span>
                    <button
                      onClick={() => void crearFichaDe(s.lavanderiaId)}
                      disabled={creandoFicha === s.lavanderiaId}
                      className="btn-secondary text-xs disabled:opacity-50"
                    >
                      {creandoFicha === s.lavanderiaId ? 'Creando…' : 'Crear ficha'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={mostrarAlta} onClose={() => setMostrarAlta(false)} title="Nuevo empleado" size="xl">
        <p className="text-xs text-sereno-dark/50 mb-4">
          Se creará también en la app de lavandería, para que aparezca en los cuadrantes y pueda fichar.
          El contrato se añade después, desde su ficha.
        </p>
        <EmpleadoForm onGuardar={crear} onCancelar={() => setMostrarAlta(false)} />
      </Modal>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">
      {children}
    </th>
  );
}

function Avatar({ nombre }: { nombre: string }) {
  const iniciales = nombre
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || '')
    .join('')
    .toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-sereno-dark text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
      {iniciales || '?'}
    </div>
  );
}

function Etiquetas({ fila }: { fila: FilaEmpleado }) {
  const avisos: { texto: string; color: string }[] = [];
  if (fila.sinContratoVigente) avisos.push({ texto: 'Sin contrato', color: 'text-amber-700 bg-amber-100' });
  if (!fila.lavanderiaId) avisos.push({ texto: 'Sin enlazar', color: 'text-amber-700 bg-amber-100' });
  if (fila.lavanderiaDesincronizada) avisos.push({ texto: 'Desincronizada', color: 'text-red-700 bg-red-100' });
  if (fila.caducado) avisos.push({ texto: 'Plazo cumplido', color: 'text-red-700 bg-red-100' });
  for (const e of fila.etiquetas) {
    if (ETIQUETA_LABEL[e]) avisos.push({ texto: ETIQUETA_LABEL[e], color: 'text-sereno-dark/50 bg-gray-100' });
  }
  if (avisos.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-0.5">
      {avisos.map((a) => (
        <span key={a.texto} className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${a.color}`}>
          {a.texto}
        </span>
      ))}
    </div>
  );
}

function Contrato({ fila }: { fila: FilaEmpleado }) {
  if (!fila.contratoVigente) {
    return fila.situacion === 'alta' ? (
      <span className="inline-flex items-center gap-1 text-amber-700">
        <AlertTriangle size={13} /> Sin contrato
      </span>
    ) : (
      <span className="text-sereno-dark/30">—</span>
    );
  }
  const { tipo, fechaFin } = fila.contratoVigente;
  return (
    <>
      <span>{TIPO_CONTRATO_LABEL[tipo] || tipo}</span>
      {fechaFin && <span className="text-sereno-dark/40"> · hasta {fechaCorta(fechaFin)}</span>}
    </>
  );
}

function Contacto({ fila }: { fila: FilaEmpleado }) {
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      {fila.telefono && (
        <a
          href={`tel:${fila.telefono}`}
          onClick={(e) => e.stopPropagation()}
          className="text-sereno-dark/40 hover:text-sereno-dark p-1"
          title={fila.telefono}
        >
          <Phone size={15} />
        </a>
      )}
      {fila.telefonoNormalizado && (
        <a
          href={`https://wa.me/${fila.telefonoNormalizado}`}
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sereno-dark/40 hover:text-green-600 p-1"
          title="WhatsApp"
        >
          <MessageCircle size={15} />
        </a>
      )}
      {!fila.telefono && !fila.lavanderiaId && <Link2Off size={15} className="text-amber-500" />}
    </div>
  );
}
